/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Security Hooks - React Query for Security Overview Dashboard
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { securityService, SecurityOverview, FailedLoginSummary, LockedAccount, OrgSessionCount } from '@/services/superadmin/security.service';
import { ApiError } from '@/services/api/types';

export const securityKeys = {
  all: ['security'] as const,
  overview: () => [...securityKeys.all, 'overview'] as const,
  failedLogins: () => [...securityKeys.all, 'failed-logins'] as const,
  lockedAccounts: () => [...securityKeys.all, 'locked-accounts'] as const,
  sessions: () => [...securityKeys.all, 'sessions'] as const,
};

export function useSecurityOverview() {
  return useQuery<SecurityOverview, ApiError>({
    queryKey: securityKeys.overview(),
    queryFn: securityService.getOverview,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useFailedLogins() {
  return useQuery<FailedLoginSummary, ApiError>({
    queryKey: securityKeys.failedLogins(),
    queryFn: securityService.getFailedLogins,
    staleTime: 30 * 1000,
  });
}

export function useLockedAccounts() {
  return useQuery<LockedAccount[], ApiError>({
    queryKey: securityKeys.lockedAccounts(),
    queryFn: securityService.getLockedAccounts,
    staleTime: 30 * 1000,
  });
}

export function useSessionsByOrg() {
  return useQuery<OrgSessionCount[], ApiError>({
    queryKey: securityKeys.sessions(),
    queryFn: securityService.getSessionsByOrg,
    staleTime: 30 * 1000,
  });
}

export function useAdminUnlockAccount() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: securityService.unlockAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.all });
    },
  });
}
